import React, { ChangeEvent, KeyboardEvent, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Order } from 'types/ShoppingCart';
import {
  removeProduct,
  setTempAmount,
  updateOrderAmount,
} from 'actions/ShoppingCart';
import { getTempAmount } from 'reducers/ShoppingCart';
import { getAllowFractionalProductQuantities } from 'reducers/configs/settings';

import {
  getAutoFocusInputValue,
  getDisableOrderQty,
  getOrderAmount,
  sanitizeValue,
} from '../utils';

const SimpleRowQuantity = React.memo<Pick<Order, 'orderIndex'>>(
  ({ orderIndex }) => {
    const dispatch = useDispatch();
    const orderAmount = useSelector(getOrderAmount(orderIndex));
    const autoFocus = useSelector(getAutoFocusInputValue(orderIndex));
    const disabled = useSelector(getDisableOrderQty(orderIndex));
    const tempAmount = useSelector(getTempAmount);
    const isFractionalQuantityAllowed = useSelector(
      getAllowFractionalProductQuantities,
    );
    const amount =
      tempAmount.orderIndex === orderIndex ? tempAmount.amount : orderAmount;

    const ref = useRef<HTMLInputElement>(null);

    const [isEmpty, setIsEmpty] = useState(false);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const qty = sanitizeValue(e.target.value);
      dispatch(setTempAmount(qty, orderIndex));
      if (Number.isNaN(Number(qty)) || !qty) {
        setIsEmpty(true);
      } else {
        setIsEmpty(false);
      }
    };

    const handleBlur = () => {
      setIsEmpty(false);
      // Always clear temp amount
      dispatch(setTempAmount(false, false));
      if (tempAmount.orderIndex !== orderIndex) {
        return; // Did not edit this row
      }
      let { amount } = tempAmount;
      if (amount === '') {
        return; // Did not enter a non-empty amount
      }
      if (Number.isNaN(Number(amount))) {
        return; // Did not enter a legal amount
      }

      // Apply rounding if configured to only accept integers
      if (!isFractionalQuantityAllowed) {
        const roundedAmount = Math.round(amount);
        // minimum amount after rounding is 1/-1
        amount =
          roundedAmount === 0 && amount !== 0
            ? Math.sign(amount)
            : roundedAmount;
      }

      // The amount is valid, commit it
      if (Number(amount) === 0) {
        dispatch(removeProduct(orderIndex));
      } else {
        dispatch(updateOrderAmount(amount, orderIndex));
      }
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        ref.current?.blur();
      }
    };

    return (
      <input
        type="text"
        ref={ref}
        id="autoFocusInput"
        value={isEmpty ? '' : amount}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onChange={handleChange}
        onClick={e => e.stopPropagation()}
        disabled={disabled}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus}
        autoComplete="off"
        onFocus={autoFocus ? e => e.target.select() : undefined}
      />
    );
  },
);

export default SimpleRowQuantity;
